import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { GridColDef, GridRenderCellParams, GridRowParams, GridActionsCellItem } from '@mui/x-data-grid';
import { Button, Box, Typography, Alert, Snackbar, Chip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { useNavigate } from 'react-router-dom';

import { DataTable } from '../components/DataTable';
import { OrganizationForm } from '../components/organizations/OrganizationForm';
import { organizationsApi } from '../api/services';
import { extractApiError } from '../utils/apiErrors';
import type { Organization } from '../api/config';

export const OrganizationsPage: React.FC = () => {
  const [isFormOpen, setIsFormOpen] = React.useState(false);
  const [selectedOrganization, setSelectedOrganization] = React.useState<Organization | undefined>();
  const [snackbar, setSnackbar] = React.useState<{ message: string; severity: 'error' | 'success' } | null>(null);
  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const { data: organizations = [], isLoading, error } = useQuery({
    queryKey: ['organizations'],
    queryFn: organizationsApi.getAll,
  });

  const createMutation = useMutation({
    mutationFn: organizationsApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      setSnackbar({ message: 'Organization created successfully', severity: 'success' });
      handleCloseForm();
    },
    onError: (error) => {
      setSnackbar({ message: extractApiError(error, 'Failed to create organization'), severity: 'error' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string | number; data: Partial<Organization> }) =>
      organizationsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      setSnackbar({ message: 'Organization updated successfully', severity: 'success' });
      handleCloseForm();
    },
    onError: (error) => {
      setSnackbar({ message: extractApiError(error, 'Failed to update organization'), severity: 'error' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: organizationsApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      setSnackbar({ message: 'Organization deleted successfully', severity: 'success' });
    },
    onError: (error) => {
      setSnackbar({ message: extractApiError(error, 'Failed to delete organization'), severity: 'error' });
    },
  });

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 35, resizable: true },
    { field: 'name', headerName: 'Name', width: 250, resizable: true },
    { field: 'short_name', headerName: 'Short Name', width: 120, resizable: true },
    { field: 'primary_contact_name', headerName: 'Contact Name', width: 200, resizable: true },
    { field: 'primary_contact_email', headerName: 'Contact Email', width: 250, resizable: true },
    { field: 'primary_contact_phone', headerName: 'Contact Phone', width: 150, resizable: true },
    {
      field: 'location_counts',
      headerName: 'Locations',
      width: 200,
      renderCell: (params: GridRenderCellParams<Organization>) => {
        const activeCount = params.row?.active_locations || 0;
        const inactiveCount = params.row?.inactive_locations || 0;
        const totalCount = params.row?.total_locations || 0;
        
        return (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            <Chip
              label={`${activeCount} active`}
              color={activeCount > 0 ? 'success' : 'default'}
              size="small"
              variant="outlined"
            />
            <Chip
              label={`${inactiveCount} inactive`}
              color={inactiveCount > 0 ? 'warning' : 'default'}
              size="small"
              variant="outlined"
            />
            <Chip
              label={`${totalCount} total`}
              color="primary"
              size="small"
              variant="outlined"
            />
          </Box>
        );
      },
      resizable: true,
    },
  ];

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setSelectedOrganization(undefined);
    createMutation.reset();
    updateMutation.reset();
  };

  const mutationError = createMutation.error || updateMutation.error;

  const handleSubmit = (data: Partial<Organization>) => {
    createMutation.reset();
    updateMutation.reset();
    if (selectedOrganization) {
      updateMutation.mutate({ id: selectedOrganization.id, data });
    } else {
      createMutation.mutate({
        name: data.name!,
        short_name: data.short_name!,
        primary_contact_name: data.primary_contact_name,
        primary_contact_phone: data.primary_contact_phone,
        primary_contact_email: data.primary_contact_email,
      });
    }
  };

  const handleEdit = (id: string) => {
    const organization = organizations.find((org) => org.id.toString() === id);
    if (organization) {
      setSelectedOrganization(organization);
      setIsFormOpen(true);
    }
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this organization?')) {
      deleteMutation.mutate(id);
    }
  };

  const customActions = (params: GridRowParams) => [
    <GridActionsCellItem
      key="locations"
      icon={<LocationOnIcon />}
      label="View Locations"
      onClick={() => navigate(`/locations?organization_id=${params.id}`)}
      showInMenu={false}
    />,
  ];

  if (error) {
    return <Alert severity="error">Error loading organizations</Alert>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5" component="h1">
          Organizations
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setIsFormOpen(true)}
        >
          Add Organization
        </Button>
      </Box>

      <DataTable
        rows={organizations}
        columns={columns}
        loading={isLoading}
        onEditClick={handleEdit}
        onDeleteClick={handleDelete}
        getRowId={(row: Organization) => row.id.toString()}
        customActions={customActions}
        fullHeight
      />

      <OrganizationForm
        open={isFormOpen}
        onClose={handleCloseForm}
        onSubmit={handleSubmit}
        initialData={selectedOrganization}
        error={mutationError ? extractApiError(mutationError) : undefined}
        isPending={createMutation.isPending || updateMutation.isPending}
      />

      <Snackbar
        open={!!snackbar}
        autoHideDuration={6000}
        onClose={() => setSnackbar(null)}
      >
        <Box>
          {snackbar && (
            <Alert 
              severity={snackbar.severity} 
              onClose={() => setSnackbar(null)}
              sx={{ width: '100%' }}
            >
              {snackbar.message}
            </Alert>
          )}
        </Box>
      </Snackbar>
    </Box>
  );
};
